import { Add, Settings } from 'almanac-ui/icons'
import cx from 'classnames'
import { useCallback, useMemo, useRef } from 'react'
import { KnowledgeBaseSnippet, KnowledgeBaseSnippetKind } from 'types'
import { SnippetsDropdown } from '../SnippetsDropdown/SnippetsDropdown'
import Styles from './SnippetsToolbar.module.scss'
import { generatePath } from 'react-router-dom'
import { BRAND_KITS_PATH } from 'paths'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useSnippets, useUpdateSnippetUsage } from 'hooks/useSnippets'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useOpenNewSnippetModal } from 'hooks/useOpenNewSnippetModal'
import { CreateKnowledgeBaseSnippetPayload } from 'api/clients/KnowledgeBaseClient'
import { Button } from 'blaze-ui/Button'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const byKinds = (kinds?: KnowledgeBaseSnippetKind[]) => (snippet: KnowledgeBaseSnippet) =>
  !kinds || kinds.includes(snippet.kind)

const SUGGESTED_SNIPPETS_COUNT = 3
const MOBILE_SNIPPETS_COUNT = 2

interface Props {
  kinds?: KnowledgeBaseSnippetKind[]
  shown?: boolean
  inputValue: string
  onInsert: (value: string) => void
  focusInput: () => void
}

export const SnippetsToolbar = ({ inputValue, onInsert, focusInput, kinds, shown = true }: Props) => {
  const toolbarRef = useRef<HTMLDivElement>(null)
  const snippetsQuery = useSnippets()
  const workspaceId = useCurrentWorkspaceId()
  const openNewSnippetModal = useOpenNewSnippetModal()
  const updateSnippetUsage = useUpdateSnippetUsage()
  const { isMobile } = useResponsiveDesign()

  const snippets = useMemo(
    () => snippetsQuery.data?.sort((a, b) => b.use_count - a.use_count)?.filter(byKinds(kinds)),
    [kinds, snippetsQuery.data],
  )
  const noSnippets = snippetsQuery.isFetched && (snippets?.length || 0) === 0
  const suggestedSnippetsCount = isMobile ? MOBILE_SNIPPETS_COUNT : SUGGESTED_SNIPPETS_COUNT
  const suggestedSnippets = useMemo(
    () => snippets?.slice(0, suggestedSnippetsCount),
    [snippets, suggestedSnippetsCount],
  )
  const moreSnippets = useMemo(() => snippets?.slice(suggestedSnippetsCount), [snippets, suggestedSnippetsCount])

  const onSelect = useCallback(
    (snippet: KnowledgeBaseSnippet) => {
      onInsert(snippet.content)
      updateSnippetUsage.mutate(snippet)
    },
    [onInsert, updateSnippetUsage],
  )

  const onSave = useCallback(
    (payload: CreateKnowledgeBaseSnippetPayload) => {
      if (payload.content === inputValue) {
        focusInput()
        return
      }
      onInsert(payload.content)
    },
    [onInsert, focusInput, inputValue],
  )

  const openSnippetModal = useCallback(() => {
    openNewSnippetModal(
      {
        onClose: focusInput,
        onSave,
        content: inputValue,
        kinds,
      },
      { newStack: true },
    )
  }, [kinds, openNewSnippetModal, inputValue, focusInput, onSave])

  return (
    <div className={cx(Styles.toolbar, { [Styles.shown]: shown })} ref={toolbarRef} data-testid="snippets-toolbar">
      <div className={Styles.suggestions}>
        {noSnippets ? (
          <Button variant="ghost" frontIcon={Add} size="xs" onPress={openSnippetModal}>
            Create a snippet
          </Button>
        ) : (
          <IconButton icon={Add} variant="ghost" size="xs" onPress={openSnippetModal} />
        )}
        {suggestedSnippets?.map((snippet) => (
          <Button variant="ghost" size="xs" key={snippet.id} onPress={() => onSelect(snippet)}>
            {snippet.name}
          </Button>
        ))}
        {moreSnippets && moreSnippets.length > 0 && (
          <SnippetsDropdown onSelect={onSelect} kinds={kinds} snippets={moreSnippets} />
        )}
      </div>
      <div className={Styles.rightActions}>
        <Tooltip label="Take me to the BrandKit page">
          <div>
            <IconButton
              icon={Settings}
              size="xs"
              variant="ghost"
              onPress={() => window.open(generatePath(BRAND_KITS_PATH, { workspaceId }), '_blank')}
            />
          </div>
        </Tooltip>
      </div>
    </div>
  )
}
